import { officeTreeOperationSchema } from '@felix/domain';
import { z } from 'zod';
import type {
  FolderTreeImpact,
  FolderTreeNode,
  FolderTreeOperation,
  FolderTreeView,
  LibraryFolder,
  LibrarySection,
} from './types.js';

/**
 * Klasör ağacı yönetiminin saf kuralları (Form Categories, Manuals ▸ "Manage folders"; ADR 0037
 * §11, ADR 0038 §8, §9). Ekran bileşenleri yalnız bunları çağırır; testleri I/O'suzdur. Kurallar
 * sunucuda (`@felix/domain` `planOfficeTreeOperation`) yeniden denetlenir; burası yalnız
 * hangi denetimin çizileceğini ve isteğin biçimini belirler.
 */

/** Klasörü yönetilen bölümler: PMS Instructions ürün klasörüdür (M8a4a M1), yönetim ekranı yok. */
export type FolderSection = Exclude<LibrarySection, 'pms_instructions'>;

/** Ekranın yolu: Form Categories (menü öğesi) ve Manuals ▸ Manage folders (menü öğesi değil). */
export const FOLDER_SCREEN_PATH: Record<FolderSection, string> = {
  ism: '/compliance/ism-forms/categories',
  manuals: '/compliance/manuals/folders',
};

/** Kardeş sırası: sıra, sonra sayısal kod ("9" < "10"), sonra anahtar (sunucuyla aynı). */
export function compareFolderNodes(
  a: Pick<FolderTreeNode, 'sortOrder' | 'code' | 'key' | 'id'>,
  b: Pick<FolderTreeNode, 'sortOrder' | 'code' | 'key' | 'id'>,
): number {
  if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
  const byCode = a.code.localeCompare(b.code, 'en', { numeric: true });
  if (byCode !== 0) return byCode;
  const ak = a.key ?? '';
  const bk = b.key ?? '';
  if (ak !== bk) return ak < bk ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** Arayüz dilindeki ad: Türkçede `nameTr`, boşsa `name` (CLAUDE.md i18n). */
export function localFolderName(
  node: Pick<FolderTreeNode, 'name' | 'nameTr'>,
  language: string,
): string {
  return language.startsWith('tr') && node.nameTr && node.nameTr.trim() !== ''
    ? node.nameTr
    : node.name;
}

/** "01. Safety Management System" */
export function localFolderLabel(
  node: Pick<FolderTreeNode, 'code' | 'name' | 'nameTr'>,
  language: string,
): string {
  return `${node.code}. ${localFolderName(node, language)}`;
}

/** Ağaçta bir satır: düğüm, düzey ve kardeşleri arasındaki yeri. */
export interface FolderRow {
  node: FolderTreeNode;
  level: number;
  /** Canlı kardeşler arasında ilk/son (sıralama düğmeleri). */
  first: boolean;
  last: boolean;
}

export interface FolderRows {
  /** Canlı ağaç, üstten alta. */
  live: FolderRow[];
  /** "Retired folders": üstü canlı (ya da yok) emekli alt ağaçlar, ağaçtaki yer sırasıyla. */
  retired: FolderRow[];
}

const isRetired = (node: Pick<FolderTreeNode, 'retiredAt'>) => node.retiredAt !== null;

/**
 * Düzenleyicinin satırları: canlı ağaç ve "Retired folders" grubu. Düzenleyicide boş emekli
 * klasör de görünür (ofis geri alabilsin); gemide gizlenir (`arrangeLibraryTree`).
 */
export function folderRows(nodes: readonly FolderTreeNode[]): FolderRows {
  const ids = new Set(nodes.map((n) => n.id));
  const children = new Map<string | null, FolderTreeNode[]>();
  for (const node of nodes) {
    const parent = node.parentId !== null && ids.has(node.parentId) ? node.parentId : null;
    children.set(parent, [...(children.get(parent) ?? []), node]);
  }
  for (const list of children.values()) list.sort(compareFolderNodes);
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const seen = new Set<string>();

  const walk = (
    node: FolderTreeNode,
    level: number,
    siblings: FolderTreeNode[],
    out: FolderRow[],
    retired: boolean,
  ) => {
    if (seen.has(node.id)) return;
    seen.add(node.id);
    const index = siblings.indexOf(node);
    out.push({ node, level, first: index <= 0, last: index === siblings.length - 1 });
    const kids = (children.get(node.id) ?? []).filter((c) => isRetired(c) === retired);
    for (const child of kids) walk(child, level + 1, kids, out, retired);
  };

  const live: FolderRow[] = [];
  const liveRoots = nodes
    .filter(
      (n) =>
        !isRetired(n) &&
        (n.parentId === null ||
          !ids.has(n.parentId) ||
          isRetired(byId.get(n.parentId) as FolderTreeNode)),
    )
    .sort(compareFolderNodes);
  for (const root of liveRoots) walk(root, 1, liveRoots, live, false);

  // Emekli kökler: üstü canlı ya da yok; ağaçtaki yer sırasıyla (canlı satırın ardından)
  const position = new Map<string, number>();
  const order = (node: FolderTreeNode) => {
    if (position.has(node.id)) return;
    position.set(node.id, position.size);
    for (const child of children.get(node.id) ?? []) order(child);
  };
  for (const root of children.get(null) ?? []) order(root);
  const retiredRoots = nodes
    .filter(
      (n) =>
        isRetired(n) &&
        (n.parentId === null ||
          !ids.has(n.parentId) ||
          !isRetired(byId.get(n.parentId) as FolderTreeNode)),
    )
    .sort((a, b) => (position.get(a.id) ?? 0) - (position.get(b.id) ?? 0));
  const retired: FolderRow[] = [];
  for (const root of retiredRoots) walk(root, 1, [root], retired, true);
  return { live, retired };
}

export type FolderBadge = 'template' | 'officeModified' | 'retired';

/**
 * Düğümün rozetleri (ADR 0038 §6, §9): şablondan gelip ofisin dokunmadığı "Template", ofis izi
 * olan (ofisin eklediği dahil) "Office modified", emekli "Retired".
 */
export function folderBadges(
  node: Pick<FolderTreeNode, 'origin' | 'officeModifiedAt' | 'retiredAt'>,
): FolderBadge[] {
  const badges: FolderBadge[] = [];
  if (node.officeModifiedAt !== null || node.origin === 'office') badges.push('officeModified');
  else if (node.origin === 'template') badges.push('template');
  if (node.retiredAt !== null) badges.push('retired');
  return badges;
}

/** Düğümün alt ağacı (kendisi dahil). */
export function subtreeIds(nodes: readonly FolderTreeNode[], id: string): Set<string> {
  const result = new Set([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const node of nodes) {
      if (!result.has(node.id) && node.parentId !== null && result.has(node.parentId)) {
        result.add(node.id);
        grew = true;
      }
    }
  }
  return result;
}

/**
 * Taşımanın hedefleri (ADR 0038 §8): aynı bölümde, canlı, düğümün kendisi ve altı dışında, anahtarı
 * olan klasörler; başta "en üst düzey" (`null`). Şu anki üst listede kalır (değişmeyen seçim).
 */
export function moveTargets(
  nodes: readonly FolderTreeNode[],
  node: FolderTreeNode,
): (FolderTreeNode | null)[] {
  const excluded = subtreeIds(nodes, node.id);
  const rows = folderRows(nodes).live.map((r) => r.node);
  return [
    null,
    ...rows.filter((n) => n.section === node.section && n.key !== null && !excluded.has(n.id)),
  ];
}

export type FolderActionKind =
  | 'add'
  | 'addChild'
  | 'rename'
  | 'move'
  | 'retire'
  | 'restore'
  | 'remove'
  | 'up'
  | 'down';

/**
 * "Remove" (ADR 0142 §4): kurulumdaki gemide ofisin açtığı, belgesiz ve alt klasörsüz (emekli
 * dahil) klasör kaldırılır. Son sözü sunucu söyler (öbür gemiler ve belge kuyruğu).
 */
export function removableFolder(
  view: Pick<FolderTreeView, 'setupState' | 'nodes'>,
  node: FolderTreeNode,
): boolean {
  return (
    view.setupState === 'setup' &&
    node.origin === 'office' &&
    node.documentCount === 0 &&
    !view.nodes.some((n) => n.parentId === node.id)
  );
}

/** Düzenleme formunun değerleri. */
export interface FolderFormValues {
  code: string;
  nameEn: string;
  nameTr: string;
  /** Taşımada yeni üstün anahtarı; `''` en üst düzey. */
  parentKey: string;
}

/** "Apply to": bu gemi, seçili gemiler ya da bütün gemiler (ADR 0037 §11). */
export type ApplyMode = 'this' | 'selected' | 'all';

export function applyTarget(
  mode: ApplyMode,
  currentVesselId: string,
  selected: readonly string[],
): 'all' | string[] {
  if (mode === 'all') return 'all';
  if (mode === 'this') return [currentVesselId];
  return [...new Set(selected)];
}

/** Formun değerleri: işlem alanları ve "Apply to". */
export interface FolderFormState extends FolderFormValues {
  mode: ApplyMode;
  vessels: string[];
}

/**
 * Formun şeması (CLAUDE.md "Form ve doğrulama": React Hook Form + Zod): alan sınırları sunucuyla
 * aynıdır (kod 32, ad 120); kurulan işlem ortak `officeTreeOperationSchema`'dan geçer. Hata
 * iletisi metin anahtarıdır (`documents.folders.form.*`).
 */
export function folderFormSchema(
  kind: FolderActionKind,
  section: LibrarySection,
  node: FolderTreeNode | null,
) {
  const named = kind === 'add' || kind === 'addChild' || kind === 'rename';
  return z
    .object({
      code: z.string(),
      nameEn: z.string(),
      nameTr: z.string(),
      parentKey: z.string(),
      mode: z.enum(['this', 'selected', 'all']),
      vessels: z.array(z.string()),
    })
    .superRefine((value, ctx) => {
      let failed = false;
      const issue = (path: string, message: string) => {
        failed = true;
        ctx.addIssue({ code: 'custom', path: [path], message });
      };
      if (named) {
        const code = value.code.trim();
        const en = value.nameEn.trim();
        if (code === '') issue('code', 'required');
        else if (code.length > 32) issue('code', 'tooLong');
        if (en === '') issue('nameEn', 'required');
        else if (en.length > 120) issue('nameEn', 'tooLong');
        if (value.nameTr.trim().length > 120) issue('nameTr', 'tooLong');
      }
      if (value.mode === 'selected' && value.vessels.length === 0) {
        issue('vessels', 'vesselsRequired');
      }
      if (failed) return;
      const operation = officeTreeOperationSchema.safeParse(
        folderOperation(kind, section, node, value),
      );
      if (!operation.success) {
        ctx.addIssue({ code: 'custom', path: ['code'], message: 'invalid' });
      }
    });
}

/** Formdan sunucunun işlemi (`officeTreeOperationSchema`). */
export function folderOperation(
  kind: FolderActionKind,
  section: LibrarySection,
  node: FolderTreeNode | null,
  values: FolderFormValues,
): FolderTreeOperation {
  const name = {
    en: values.nameEn.trim(),
    tr: values.nameTr.trim() === '' ? null : values.nameTr.trim(),
  };
  const key = node?.key ?? '';
  switch (kind) {
    case 'add':
      return { type: 'add', section, parentKey: null, code: values.code.trim(), name };
    case 'addChild':
      return { type: 'add', section, parentKey: key, code: values.code.trim(), name };
    case 'rename':
      return { type: 'rename', key, code: values.code.trim(), name };
    case 'move':
      return { type: 'move', key, parentKey: values.parentKey === '' ? null : values.parentKey };
    case 'retire':
      return { type: 'retire', key };
    case 'restore':
      return { type: 'restore', key };
    case 'remove':
      return { type: 'remove', key };
    case 'up':
      return { type: 'reorder', key, direction: 'up' };
    case 'down':
      return { type: 'reorder', key, direction: 'down' };
  }
}

/** Formun başlangıç değerleri: adlandırmada düğümün bugünkü kodu ve adları. */
export function initialFolderValues(
  kind: FolderActionKind,
  node: FolderTreeNode | null,
): FolderFormValues {
  if (kind === 'rename' && node) {
    return {
      code: node.code,
      nameEn: node.name,
      nameTr: node.nameTr ?? '',
      parentKey: node.parentKey ?? '',
    };
  }
  return { code: '', nameEn: '', nameTr: '', parentKey: node?.parentKey ?? '' };
}

/**
 * Sıralamadan sonra odak (WAI-ARIA: odak taşınan düğümde kalır): `rows` sıralamadan önceki canlı
 * satırlardır. Düğüm yeni yerinde aynı yöndeki düğmeyi alır; uca vardıysa o düğme pasif olur ve
 * odak öteki yöndeki düğmeye geçer.
 */
export function focusAfterReorder(
  rows: readonly FolderRow[],
  key: string,
  direction: 'up' | 'down',
): { key: string; control: 'up' | 'down' } {
  const row = rows.find((r) => r.node.key === key);
  if (!row) return { key, control: direction };
  const siblings = rows.filter(
    (r) => r.level === row.level && r.node.parentId === row.node.parentId,
  );
  const index = siblings.indexOf(row);
  const next = Math.max(0, Math.min(siblings.length - 1, index + (direction === 'up' ? -1 : 1)));
  if (siblings.length > 1 && next === 0) return { key, control: 'down' };
  if (siblings.length > 1 && next === siblings.length - 1) return { key, control: 'up' };
  return { key, control: direction };
}

/** Emekliye ayırma onayında gösterilecek gemiler: düğümü taşıyanlar önce. */
export function impactRows(
  impact: readonly FolderTreeImpact[],
  target: 'all' | readonly string[],
): FolderTreeImpact[] {
  const wanted = target === 'all' ? null : new Set(target);
  return impact
    .filter((i) => wanted === null || wanted.has(i.vesselId))
    .sort((a, b) => Number(b.present) - Number(a.present) || a.name.localeCompare(b.name));
}

// --- Kütüphane ağacının gösterim grupları (Form List, Form Categories; ADR 0038 §3, §9) ---

export const RETIRED_GROUP_ID = '__retired_folders__';
export const OTHER_GROUP_ID = '__other_company_documents__';

/** Klasörü ağaçta olmayan ya da sunucunun gruba koyduğu belgeler. */
export function otherDocumentIds(tree: {
  folders: readonly LibraryFolder[];
  documents: readonly { id: string; folderId: string }[];
  otherCompanyDocumentIds?: readonly string[] | undefined;
}): Set<string> {
  const ids = new Set(tree.folders.map((f) => f.id));
  const other = new Set(tree.otherCompanyDocumentIds ?? []);
  for (const doc of tree.documents) if (!ids.has(doc.folderId)) other.add(doc.id);
  return other;
}
